import { useRouter } from "next/router";
import { createContext, PropsWithChildren, useEffect, useMemo } from "react";
import { useNavigateSameTab } from "../helpers/navigation";
import { LOGGED_USER_ID, SSO_REDIRECT_PARAM } from "../dataAccess/constants";
import { pageMapping } from "../builder/mapping/page";
import { User } from "../dataAccess/api/user";
import { JWTError, useSession } from "../dataAccess/storages/session";
import {
  buildUrlWithQueryParams,
  useHash,
  useHashParams,
  useQueryParams,
} from "../dataAccess/QueryParams";
import { decodeState } from "../helpers/sso";
import { localStorageWrapper } from "../dataAccess/LocalStorage";
import { A11yStatus } from "../components/ui/A11yStatus";
import { useTranslation } from "../helpers/i18n";

interface Ctx {
  user?: User;
  loading: boolean;
  jwtAuthenticationError: JWTError | null;
}

const initialState = {
  loading: false,
  jwtAuthenticationError: null,
};

export const UserCtx = createContext<Ctx>(initialState);

export function UserProvider({ children }: PropsWithChildren) {
  const { jwt } = useQueryParams();
  const { hash } = useHash();
  const { id_token, state } = useHashParams();
  const { loading, user, jwtAuthenticationError } = useSession();
  const router = useRouter();
  const { replace } = useNavigateSameTab();
  const { t } = useTranslation();

  const context = useMemo(
    () => ({
      user,
      loading,
      jwtAuthenticationError,
    }),
    [user, loading, jwtAuthenticationError],
  );

  useEffect(() => {
    if (jwt) {
      const { jwt, cid, ...params } = router.query;
      replace(
        buildUrlWithQueryParams(
          router.asPath,
          { ...params },
          { keepCurrentQuery: false },
        ),
        {
          shallow: true,
        },
      );
    }
    if (id_token) {
      let { retUrl } = decodeState(state || "");
      if (
        retUrl?.includes(pageMapping.Login.path) ||
        retUrl?.includes(pageMapping.SignUp.path)
      ) {
        retUrl = "/";
      }
      const currentPath = retUrl || router.asPath.replace(hash, "");
      const { [SSO_REDIRECT_PARAM]: ssoredirect, ...params } = router.query;
      replace(
        buildUrlWithQueryParams(
          currentPath,
          { ...params },
          { keepCurrentQuery: false },
        ),
        {
          shallow: true,
        },
      );
    }
  }, []);

  useEffect(() => {
    const statusEl = document.getElementById("bf-profile-status");
    if (!statusEl) {
      return;
    }

    const prevId = localStorageWrapper.getItem(LOGGED_USER_ID);

    if (user) {
      const userId = String(user.id);
      if (prevId !== userId) {
        localStorageWrapper.setItem(LOGGED_USER_ID, userId);
        statusEl.textContent = t("accessibility_authentication_successful");
      }
    } else if (prevId === "logged_out") {
      localStorageWrapper.setItem(LOGGED_USER_ID, "");
      statusEl.textContent = t("accessibility_logout_successful");
    }
  }, [user]);

  return (
    <UserCtx.Provider value={context}>
      <A11yStatus role="status" id="bf-profile-status" />
      {children}
    </UserCtx.Provider>
  );
}
